import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  InventoryCategoryListItem,
  InventoryCategoryOption,
  InventoryCategoryRequest,
  InventoryItemDetail,
  InventoryItemListItem,
  InventoryItemOption,
  InventoryItemRequest,
  InventoryItemTypeListItem,
  InventoryItemTypeOption,
  InventoryItemTypeRequest,
  StorageFacilityListItem,
  StorageFacilityOption,
  StorageFacilityRequest,
  UnitOfMeasureListItem,
  UnitOfMeasureOption,
  UnitOfMeasureRequest,
} from './inventoryTypes';

/** The item list's own axes, on top of the base query's status. */
export interface InventoryItemListQuery extends ListQuery {
  category?: string;
  kind?: string;
  tracking?: string;
}

/** The facility list's own axes. */
export interface StorageFacilityListQuery extends ListQuery {
  type?: string;
  unit?: string;
}

/**
 * Serializes the base list query.
 *
 * Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
 * the reader saying "none of these", which matches no row. A truthiness check would drop it and
 * the server would read the axis as unmentioned, which is the opposite result.
 */
function base(query: ListQuery, params: URLSearchParams): URLSearchParams {
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  return params;
}

function toQueryString(params: URLSearchParams): string {
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

function catalogQuery(query: ListQuery): string {
  return toQueryString(base(query, new URLSearchParams()));
}

function itemQuery(query: InventoryItemListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.category !== undefined) params.set('category', query.category);
  if (query.kind !== undefined) params.set('kind', query.kind);
  if (query.tracking !== undefined) params.set('tracking', query.tracking);

  return toQueryString(params);
}

function facilityQuery(query: StorageFacilityListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.type !== undefined) params.set('type', query.type);
  if (query.unit !== undefined) params.set('unit', query.unit);

  return toQueryString(params);
}

export const inventoryCategoriesApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<InventoryCategoryListItem>>(`/api/inventory-categories${catalogQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<InventoryCategoryListItem>(`/api/inventory-categories/${id}`, { signal }),

  /** Active categories for the item form's picker. Unpaged. */
  options: (signal?: AbortSignal) =>
    api<InventoryCategoryOption[]>('/api/inventory-categories/options', { signal }),

  create: (request: InventoryCategoryRequest) =>
    api<InventoryCategoryListItem>('/api/inventory-categories', { method: 'POST', body: request }),

  update: (id: string, request: InventoryCategoryRequest) =>
    api<InventoryCategoryListItem>(`/api/inventory-categories/${id}`, { method: 'PUT', body: request }),

  deactivate: (id: string) => api<void>(`/api/inventory-categories/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/inventory-categories/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/inventory-categories/clear', { method: 'POST' }),
};

export const inventoryItemTypesApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<InventoryItemTypeListItem>>(`/api/inventory-item-types${catalogQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<InventoryItemTypeListItem>(`/api/inventory-item-types/${id}`, { signal }),

  options: (signal?: AbortSignal) =>
    api<InventoryItemTypeOption[]>('/api/inventory-item-types/options', { signal }),

  create: (request: InventoryItemTypeRequest) =>
    api<InventoryItemTypeListItem>('/api/inventory-item-types', { method: 'POST', body: request }),

  update: (id: string, request: InventoryItemTypeRequest) =>
    api<InventoryItemTypeListItem>(`/api/inventory-item-types/${id}`, { method: 'PUT', body: request }),

  deactivate: (id: string) => api<void>(`/api/inventory-item-types/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/inventory-item-types/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/inventory-item-types/clear', { method: 'POST' }),
};

export const unitsOfMeasureApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<UnitOfMeasureListItem>>(`/api/units-of-measure${catalogQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<UnitOfMeasureListItem>(`/api/units-of-measure/${id}`, { signal }),

  options: (signal?: AbortSignal) =>
    api<UnitOfMeasureOption[]>('/api/units-of-measure/options', { signal }),

  create: (request: UnitOfMeasureRequest) =>
    api<UnitOfMeasureListItem>('/api/units-of-measure', { method: 'POST', body: request }),

  update: (id: string, request: UnitOfMeasureRequest) =>
    api<UnitOfMeasureListItem>(`/api/units-of-measure/${id}`, { method: 'PUT', body: request }),

  deactivate: (id: string) => api<void>(`/api/units-of-measure/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/units-of-measure/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/units-of-measure/clear', { method: 'POST' }),
};

export const inventoryItemsApi = {
  list: (query: InventoryItemListQuery, signal?: AbortSignal) =>
    api<PagedResult<InventoryItemListItem>>(`/api/inventory-items${itemQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<InventoryItemDetail>(`/api/inventory-items/${id}`, { signal }),

  /** Active items for a stock form's picker, each saying how its stock is identified. */
  options: (signal?: AbortSignal) =>
    api<InventoryItemOption[]>('/api/inventory-items/options', { signal }),

  create: (request: InventoryItemRequest) =>
    api<InventoryItemDetail>('/api/inventory-items', { method: 'POST', body: request }),

  update: (id: string, request: InventoryItemRequest) =>
    api<InventoryItemDetail>(`/api/inventory-items/${id}`, { method: 'PUT', body: request }),

  /** Soft delete. The SKU stays held — it is never reissued. */
  deactivate: (id: string) => api<void>(`/api/inventory-items/${id}`, { method: 'DELETE' }),

  /** Owes no SKU clash check: the code was never freed for anything to take. */
  activate: (id: string) => api<void>(`/api/inventory-items/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/inventory-items/clear', { method: 'POST' }),
};

export const storageFacilitiesApi = {
  list: (query: StorageFacilityListQuery, signal?: AbortSignal) =>
    api<PagedResult<StorageFacilityListItem>>(`/api/storage-facilities${facilityQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<StorageFacilityListItem>(`/api/storage-facilities/${id}`, { signal }),

  /** Active facilities, each carrying its full path, for the parent picker. */
  options: (signal?: AbortSignal) =>
    api<StorageFacilityOption[]>('/api/storage-facilities/options', { signal }),

  create: (request: StorageFacilityRequest) =>
    api<StorageFacilityListItem>('/api/storage-facilities', { method: 'POST', body: request }),

  update: (id: string, request: StorageFacilityRequest) =>
    api<StorageFacilityListItem>(`/api/storage-facilities/${id}`, { method: 'PUT', body: request }),

  /** Refused while live stores are nested inside it. */
  deactivate: (id: string) => api<void>(`/api/storage-facilities/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/storage-facilities/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/storage-facilities/clear', { method: 'POST' }),
};
